import EnumProvider from "@/providers/EnumProvider";
import {SelectListItem} from "@renta-apps/athenaeum-react-components";
import {RentalItemActionType, WorkOrderContractType} from "@/models/Enums";
import UnleashHelper from "@/helpers/UnleashHelper";
import FeatureFlags from "@/helpers/FeatureFlags";

/**
 * This module provides an extension of the autogenerated EnumProvider with additional logic
 * that utilizes feature flags defined in UnleashHelper. It contains overridden functions
 * from the EnumProvider that require conditional logic based on feature flags.
 *

 * Functions in this module should:
 * - Override corresponding functions in the autogenerated EnumProvider.
 * - Incorporate feature flag checks using UnleashHelper to conditionally modify behavior.
 *
 * - The `getWorkOrderContractTypeItems` function filters out certain contract types
 *   based on the state of the `DepotWork` feature flag.
 */

const FeatureFlagsAwareEnumProvider = {
    
    /**
     * Contract types selectable on a work order, minus the ones their feature flag turns off.
     *
     * @param hideAbsenceContractType Drop {@link WorkOrderContractType.Absence} from the list even
     * when {@link FeatureFlags.AbsenceFields} is on, because absences are managed through absence
     * management rather than picked here. Only honoured while {@link FeatureFlags.AbsenceHideContractType}
     * is on. Pass false for a work order that already is an absence one, so its stored value still has a
     * matching item, and for filter dropdowns, which need to be able to filter by it.
     */
    getWorkOrderContractTypeItems: (reverse: boolean = false, hideAbsenceContractType: boolean = false): SelectListItem[] => {
        const isDepotEnabled: boolean = UnleashHelper.isEnabled(FeatureFlags.DepotWork);
        const isFieldServiceEnabled: boolean = UnleashHelper.isEnabled(FeatureFlags.FieldService);
        const isAbsenceEnabled: boolean = UnleashHelper.isEnabled(FeatureFlags.AbsenceFields);
        const isAbsenceHiddenForCreate: boolean = hideAbsenceContractType && UnleashHelper.isEnabled(FeatureFlags.AbsenceHideContractType);
        let items: SelectListItem[] = EnumProvider.getWorkOrderContractTypeItems(reverse);
        if (!isDepotEnabled) {
            items = items.filter(item => Number(item.value) !== WorkOrderContractType.DepotWork);
        }
        if (!isFieldServiceEnabled) {
            items = items.filter(item => Number(item.value) !== WorkOrderContractType.FieldService);
        }
        if (!isAbsenceEnabled || isAbsenceHiddenForCreate) {
            items = items.filter(item => Number(item.value) !== WorkOrderContractType.Absence);
        }
        return items;
    },

    getRentalItemActionTypeItems: (contractType: WorkOrderContractType, reverse: boolean = false): SelectListItem[] => {
        const fieldServiceOnlyTypes: RentalItemActionType[] = [
            RentalItemActionType.FieldService,
            RentalItemActionType.ServiceDone,
            RentalItemActionType.RequiresReplacement,
        ];
        let items: SelectListItem[] = EnumProvider.getRentalItemActionTypeItems(reverse);
        if (contractType !== WorkOrderContractType.FieldService) {
            items = items.filter(item => !fieldServiceOnlyTypes.includes(Number(item.value)));
        }
        return items;
    },

    /**
     * Action types selectable for an individual rental item.
     * On a {@link WorkOrderContractType.FieldService} work order the non-service types
     * ({@link RentalItemActionType.Leave} and {@link RentalItemActionType.Return}) are not applicable.
     *
     * @param currentActionType Action type the item is currently saved with. It is never filtered out,
     * so already stored values stay visible and selectable. Omit it for new items.
     */
    getIndividualRentalItemActionTypeItems: (contractType: WorkOrderContractType, reverse: boolean = false, currentActionType: RentalItemActionType | null = null): SelectListItem[] => {
        const nonServiceTypes: RentalItemActionType[] = [
            RentalItemActionType.Leave,
            RentalItemActionType.Return,
        ];
        let items: SelectListItem[] = FeatureFlagsAwareEnumProvider.getRentalItemActionTypeItems(contractType, reverse);
        if (contractType === WorkOrderContractType.FieldService) {
            items = items.filter(item => (!nonServiceTypes.includes(Number(item.value))) || (Number(item.value) === currentActionType));
        }
        return items;
    },

};

export default FeatureFlagsAwareEnumProvider;